import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setBaseUrl, setDefaultHeaders } from "@coralblack/flax";
import { LoLang } from "../../common/common.types";
import { AwakeAuthTokenResponse, CreateAuthTokenResponse } from "../../sso/AuthToken.dto";

// Set baseUrl
setBaseUrl(process.env.REACT_APP_CDMS_BE_URL || process.env.REACT_APP_CDMS_URL || process.env.REACT_APP_SERVICE_HOST || "http://127.0.0.1");

export interface SessionState {
  token: string | null;
  expiryAt: Date | null;
}

export interface LocaleState {
  lolang: LoLang;
}

// Cookie Helper
const COOKIE_DEFAULT_PATH = "/";

// Set cookie
export const setCookie = (key: string, value: string, expiryAt: Date, opts?: { domain?: string; secure?: boolean; sameSite?: "None" }) => {
  const options = [
    opts?.domain ? `domain=${opts.domain};` : "",
    opts?.secure ? "Secure;" : "",
    opts?.sameSite ? `SameSite=${opts.sameSite};` : "",
    `expires=${expiryAt.toUTCString()};`,
    `path=${COOKIE_DEFAULT_PATH};`,
  ].filter(e => e);

  document.cookie = `${key}=${encodeURIComponent(value)}; ${options.join(" ")}`;
};

// Get cookie
export const getCookie = (key: string): string => {
  const cookie = document.cookie.match("(^|;)\\s*" + key + "\\s*=\\s*([^;]+)");
  const found = cookie ? cookie.pop() : "";

  return decodeURIComponent(found);
};

// Delete cookie
export const deleteCookie = (key: string, opts?: { domain?: string }) => {
  const options = [opts?.domain ? `domain=${opts.domain};` : "", `path=${COOKIE_DEFAULT_PATH};`].filter(e => e);

  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${options.join(" ")}`;
};

const appStage = process.env.REACT_APP_STAGE || process.env.VITE_APP_STAGE;
const featureLangCookieEnabled = (process.env.REACT_APP_FEATURE_LANG_COOKIE || process.env.VITE_APP_FEATURE_LANG_COOKIE) === "true";

export const DEVICE_KEY = "cream:auth:device:key";
export const SESSION_KEY = "cream:auth";
export const LOCALE_LANG_KEY = "LoLang";
export const LOCALE_LANG_COOKIE_KEY = `cream:lang:${appStage}`;

export const SESSION_COOKIE_DOMAIN = ".mavenclinical.com";
export const DEVICE_ENV_KEY = `${DEVICE_KEY}:${process.env.REACT_APP_STAGE || process.env.VITE_APP_STAGE}`;

const SESSION_COOKIE_KEY = `${SESSION_KEY}:${process.env.REACT_APP_STAGE || process.env.VITE_APP_STAGE}`;
const stored = localStorage.getItem(SESSION_KEY) || getCookie(SESSION_COOKIE_KEY);

export const getBrowserDefaultLanguage = () => {
  switch (navigator.language.toLocaleLowerCase()) {
    case "ko":
    case "ko-kr":
      return LoLang.KO;
    case "fr":
    case "fr-fr":
      return LoLang.FR;
    case "nl":
    case "nl-nl":
      return LoLang.NL;
    default:
      return LoLang.EN;
  }
};

if (!featureLangCookieEnabled && !localStorage.getItem(LOCALE_LANG_KEY)) {
  localStorage.setItem(LOCALE_LANG_KEY, getBrowserDefaultLanguage());
}

const language = !featureLangCookieEnabled
  ? localStorage.getItem(LOCALE_LANG_KEY)
  : getCookie(LOCALE_LANG_COOKIE_KEY) || getBrowserDefaultLanguage();

const initialState: SessionState = Object.assign(
  {
    token: null,
    expiryAt: null,
  },
  stored ? JSON.parse(stored) : {}
);

const localeInitialState: LocaleState = Object.assign({ lolang: LoLang.KO }, language ? { lolang: language } : {});

document.documentElement?.setAttribute("lang", String(localeInitialState.lolang).toLowerCase());

export const configurableDefaultHeaders = (headers?: { [key: string]: string }) =>
  Object.assign(
    {
      Authorization: initialState.token,
      Lang: language || LoLang.KO,
      "Timezone-Offset": -new Date().getTimezoneOffset(),
    },
    headers || {}
  ) as { [key: string]: string };

setDefaultHeaders(configurableDefaultHeaders());

export const setAdditionalDefaultHeaders = (headers: { [key: string]: string }) => setDefaultHeaders(headers);

const storeSessionCookie = (stringifiedPayload: string, expiryAt: Date) => {
  const isLocal = !window.location.protocol.startsWith("https");

  setCookie(SESSION_COOKIE_KEY, stringifiedPayload, new Date(expiryAt), {
    domain: SESSION_COOKIE_DOMAIN,
    secure: !isLocal,
    sameSite: !isLocal ? "None" : undefined,
  });
};

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    signIn(state, action: PayloadAction<{ redirectTo: string } & CreateAuthTokenResponse>) {
      state.token = action.payload.authToken.token;
      state.expiryAt = action.payload.authToken.expiryAt;

      const stringifiedPayload = JSON.stringify({ ...state, redirectTo: undefined });

      storeSessionCookie(stringifiedPayload, state.expiryAt);
      localStorage.setItem(SESSION_KEY, stringifiedPayload);

      if (action.payload.redirectTo) {
        if (action.payload.redirectTo.includes(":")) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [_, restProtocol] = action.payload.redirectTo.split(":");
          const [hostname] = (restProtocol || "").slice(2).split("/");
          const domain = String((hostname || "").split(".").slice(-2)[0]).toLowerCase(); // TLD는 1차(.com, .dev, .io, ..)만으로 간주함

          if (
            !["mavencdms", "mavenclinical"]
              .concat(["local", "shared", "develop"].includes(appStage) ? ["localhost", "127.0.0.1"] : [])
              .includes(domain)
          ) {
            document.location.href = "/";
            return;
          }
        }

        document.location.href = action.payload.redirectTo;
      } else {
        document.location.reload();
      }
    },
    signOut(state) {
      state.token = null;
      state.expiryAt = null;

      localStorage.removeItem(SESSION_KEY);
      deleteCookie(SESSION_COOKIE_KEY, { domain: SESSION_COOKIE_DOMAIN });

      document.location.href = "/";
    },
    refreshSignIn(state, action: PayloadAction<{} & CreateAuthTokenResponse>) {
      state.token = action.payload.authToken.token;
      state.expiryAt = action.payload.authToken.expiryAt;

      const stringifiedPayload = JSON.stringify({ ...state, redirectTo: undefined });

      storeSessionCookie(stringifiedPayload, state.expiryAt);
      localStorage.setItem(SESSION_KEY, stringifiedPayload);

      setDefaultHeaders(configurableDefaultHeaders({ Authorization: action.payload.authToken.token }));
    },
    updateExpiryAt(state, action: PayloadAction<{} & AwakeAuthTokenResponse>) {
      state.expiryAt = action.payload.authToken.expiryAt;

      const stringifiedPayload = JSON.stringify({ ...state, redirectTo: undefined });

      storeSessionCookie(stringifiedPayload, state.expiryAt);
      localStorage.setItem(SESSION_KEY, stringifiedPayload);
    },
  },
});

export const localeSlice = createSlice({
  name: "locale",
  initialState: localeInitialState,
  reducers: {
    changeLocaleLanguage(state, action: PayloadAction<{} & { lolang: LoLang }>) {
      state.lolang = action.payload.lolang;

      localStorage.setItem(LOCALE_LANG_KEY, state.lolang);
      document.documentElement?.setAttribute("lang", String(state.lolang).toLowerCase());
    },
  },
});
